import { configObject } from "src/awsconfig";
import { COGNITO_USERPOOL_WEBCLIENT_ID } from "../constants/constants";
import { decodeJwtPayload } from "./jwt";
import {
  COGNITO_LAST_AUTH_USER_FIELD,
  COGNITO_STORAGE_KEY_PREFIX,
} from "./storedSessionKeys";

export interface HighboardTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

/**
 * 이 앱 Cognito app client 의 localStorage 키 접두어(`CognitoIdentityServiceProvider.<clientId>`).
 * clientId 는 환경(prod/dev)마다 달라 런타임 SSM 값에서 읽는다. 값이 없으면 null 이다.
 */
const cognitoKeyPrefix = (): string | null => {
  const clientId = configObject[COGNITO_USERPOOL_WEBCLIENT_ID];
  return clientId ? `${COGNITO_STORAGE_KEY_PREFIX}.${clientId}` : null;
};

/**
 * Amplify v6 가 마지막 로그인의 사용자 이름을 두는 키(`<prefix>.LastAuthUser`).
 * 저장된 세션을 읽는 쪽(`readStoredUsername`)과 SSO 주입(`injectCognitoSession`)이 이 함수로 같은 키를 만든다.
 */
const lastAuthUserKey = (prefix: string): string =>
  `${prefix}.${COGNITO_LAST_AUTH_USER_FIELD}`;

/** 이 파일이 읽거나 쓰는 사용자별 세션 필드 */
type UserSessionField =
  | "idToken"
  | "accessToken"
  | "refreshToken"
  | "clockDrift"
  | "signInDetails";

/**
 * Amplify v6 가 사용자별 세션 필드를 두는 키(`<prefix>.<username>.<field>`).
 * 저장된 ID 토큰을 읽는 쪽(`readStoredIdToken`)과 SSO 주입(`injectCognitoSession`)이 이 함수로 같은 키를 만든다.
 */
const userSessionKey = (
  prefix: string,
  username: string,
  field: UserSessionField
): string => `${prefix}.${username}.${field}`;

/**
 * 이 브라우저의 Cognito 세션 키(`CognitoIdentityServiceProvider.<clientId>.*`)를 네트워크 없이 모두 지운다.
 * 다른 clientId 의 키와 무관한 키는 남긴다. clientId 를 모르거나 localStorage 가 없으면(SSR) 무동작이다.
 * throw 하지 않는다 — 앱 부팅과 로그아웃 폴백에서 불린다.
 *
 * SSO 주입 전 정리, 버려진 점유 취소, 로그아웃 상한 초과 폴백이 같은 정리를 쓴다.
 */
export const clearCognitoSessionStorage = (): void => {
  const prefix = cognitoKeyPrefix();
  if (!prefix || typeof localStorage === "undefined") return;

  const keyPrefix = `${prefix}.`;
  try {
    const sessionKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(keyPrefix)) sessionKeys.push(key);
    }
    sessionKeys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // 저장소 접근이 막힌 브라우저 — 지울 세션도 쓸 수 없었다
  }
};

/**
 * 이 브라우저에 저장된 로그인의 사용자 이름 — Amplify 가 쓰는 `<prefix>.LastAuthUser` 값이다.
 * 네트워크를 쓰지 않으며, 없거나 SSR 이거나 clientId 를 모르거나 읽을 수 없으면 null 이다.
 */
export const readStoredUsername = (): string | null => {
  const prefix = cognitoKeyPrefix();
  if (!prefix || typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(lastAuthUserKey(prefix));
  } catch {
    return null;
  }
};

/**
 * 이 브라우저에 로컬 Cognito 세션이 있는지 — Amplify 가 쓰는 `<prefix>.LastAuthUser` 키의 존재로 본다.
 * 네트워크를 쓰지 않으며, SSR 이거나 clientId 를 모르면 false 다.
 */
export const hasCognitoSession = (): boolean => readStoredUsername() !== null;

/**
 * 이 브라우저에 지금 저장된 로그인의 ID 토큰(`<prefix>.<LastAuthUser>.idToken`) 원문. 없거나 읽을 수 없으면 null.
 * 토큰 문자열을 그대로 돌려줄 뿐 내용을 해석하지 않는다 — 요청에 실린 자격이 지금 로그인의 것인지 비교하는 데 쓴다.
 */
export const readStoredIdToken = (): string | null => {
  const prefix = cognitoKeyPrefix();
  const username = readStoredUsername();
  if (!prefix || username === null) return null;
  try {
    return localStorage.getItem(userSessionKey(prefix, username, "idToken"));
  } catch {
    return null;
  }
};

/**
 * 액세스 토큰이 가리키는 Cognito 사용자 이름(`username` → `cognito:username` → `email` 순). 없으면 null.
 * 주입이 세션 키(`LastAuthUser`·`<prefix>.<username>.*`)에 쓰는 이름과 같다 — 계정이 같은지 비교하는 데만 쓰고,
 * 로그인 식별(인증 시각 등)에는 쓰지 않는다.
 */
export const usernameFromAccessToken = (accessToken: string): string | null => {
  const payload = decodeJwtPayload(accessToken);
  const username =
    payload["username"] ?? payload["cognito:username"] ?? payload["email"];
  return typeof username === "string" && username !== "" ? username : null;
};

/**
 * 하이보드 SSO 자동 로그인용 — 백엔드가 발급한 Cognito 토큰을
 * Amplify v6 기본 localStorage 형식으로 직접 써넣어 세션을 복원한다.
 *
 * Amplify v6는 토큰 저장소 커스터마이징이 없으므로(awsconfig.ts:63-84) 기본값인
 * localStorage를 사용한다. 저장 key 는 `lastAuthUserKey`·`userSessionKey` 가 만든다 —
 * Amplify 와 저장된 세션을 읽는 함수(`readStoredUsername`·`readStoredIdToken`)가 같은 키로 이 세션을 찾는다.
 *
 * clientId는 환경(prod/dev)마다 다르므로 런타임 SSM 값(configObject)에서 읽는다 —
 * 하드코딩하면 환경 전환 시 key 불일치로 세션 복원이 실패한다.
 */
export const injectCognitoSession = ({
  idToken,
  accessToken,
  refreshToken,
}: HighboardTokens): void => {
  const prefix = cognitoKeyPrefix();
  if (!prefix) {
    throw new Error(
      "[injectCognitoSession] Cognito clientId를 SSM에서 읽을 수 없습니다."
    );
  }

  // accessToken payload에서 username 추출 (이 user pool은 email = username)
  const username = usernameFromAccessToken(accessToken);
  if (!username) {
    throw new Error(
      "[injectCognitoSession] 토큰에서 username을 추출할 수 없습니다."
    );
  }

  // 기존 세션 데이터 전체 삭제 — SSO 로그인 전 다른 사용자로 로그인돼 있던 경우
  // 이전 username의 잔여 키(userData, idToken 등)가 남으면 localStorage가 오염된다.
  clearCognitoSessionStorage();

  const signInDetails = JSON.stringify({
    loginId: username,
    authFlowType: "CUSTOM_AUTH",
  });

  localStorage.setItem(lastAuthUserKey(prefix), username);
  localStorage.setItem(userSessionKey(prefix, username, "idToken"), idToken);
  localStorage.setItem(
    userSessionKey(prefix, username, "accessToken"),
    accessToken
  );
  localStorage.setItem(
    userSessionKey(prefix, username, "refreshToken"),
    refreshToken
  );
  const iat = decodeJwtPayload(idToken)["iat"] as number;
  const clockDrift = iat ? Math.floor(Date.now() / 1000) - iat : 0;
  localStorage.setItem(
    userSessionKey(prefix, username, "clockDrift"),
    String(clockDrift)
  );
  localStorage.setItem(
    userSessionKey(prefix, username, "signInDetails"),
    signInDetails
  );
};
